'use client';

import { useEffect, useId, useRef, useState, type ComponentType, type KeyboardEvent } from 'react';
import { Search } from 'lucide-react';

import { NEARBY_MIN_ZOOM, sameMapPoint, type MapFrame, type MapView, type NearbyPin } from '@/components/maps/place-view';
import { useLocale } from '@/lib/i18n';

export type ChosenPlace = {
  name?: string;
  city: string;
  address: string;
  country: string;
  latitude: number;
  longitude: number;
};

type MapProps = {
  latitude: number | null;
  longitude: number | null;
  onPick?: (latitude: number, longitude: number) => void;
  onView?: (view: MapView) => void;
  frame?: MapFrame | null;
  nearby?: NearbyPin[];
  onChooseNearby?: (pin: NearbyPin) => void;
  chosenName?: string;
  className?: string;
};

type SearchHit = {
  label: string;
  city: string;
  address: string;
  country: string;
  latitude: number;
  longitude: number;
  zoom: number;
  selectable: boolean;
};

type NearbyAgency = NearbyPin & { address: string };

const placeKeys = ['city', 'address', 'country', 'latitude', 'longitude'] as const;

function readPlace(payload: unknown): ChosenPlace | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const record = payload as Record<string, unknown>;
  if (!placeKeys.every((key) => key in record)) return null;
  if (typeof record.city !== 'string' || record.city.trim() === '') return null;
  if (typeof record.address !== 'string' || typeof record.country !== 'string') return null;
  if (typeof record.latitude !== 'number' || typeof record.longitude !== 'number') return null;
  const name = typeof record.name === 'string' ? record.name : undefined;
  return {
    name,
    city: record.city,
    address: record.address,
    country: record.country,
    latitude: record.latitude,
    longitude: record.longitude,
  };
}

function readSearch(payload: unknown): SearchHit[] {
  if (typeof payload !== 'object' || payload === null || !('results' in payload)) return [];
  const results = (payload as { results: unknown }).results;
  if (!Array.isArray(results)) return [];
  return results.flatMap((row) => {
    const place = readPlace(row);
    if (place === null || typeof row !== 'object' || row === null) return [];
    const record = row as Record<string, unknown>;
    if (typeof record.label !== 'string' || typeof record.zoom !== 'number' || typeof record.selectable !== 'boolean') return [];
    return [{ ...place, label: record.label, zoom: record.zoom, selectable: record.selectable }];
  });
}

function readNearby(payload: unknown): NearbyAgency[] {
  if (typeof payload !== 'object' || payload === null || !('agencies' in payload)) return [];
  const agencies = (payload as { agencies: unknown }).agencies;
  if (!Array.isArray(agencies)) return [];
  return agencies.flatMap((row) => {
    if (typeof row !== 'object' || row === null) return [];
    const record = row as Record<string, unknown>;
    if (typeof record.id !== 'string' || typeof record.name !== 'string') return [];
    if (typeof record.latitude !== 'number' || typeof record.longitude !== 'number') return [];
    return [{
      id: record.id,
      name: record.name,
      city: typeof record.city === 'string' ? record.city : '',
      address: typeof record.address === 'string' ? record.address : '',
      latitude: record.latitude,
      longitude: record.longitude,
    }];
  });
}

function searchParts(label: string): { title: string; detail: string } {
  const [title, ...rest] = label.split(',');
  return { title: title.trim(), detail: rest.join(',').trim() };
}

function viewKey(view: MapView): string {
  return [view.zoom.toFixed(1), view.south.toFixed(3), view.west.toFixed(3), view.north.toFixed(3), view.east.toFixed(3)].join(':');
}

export function LocationPicker({
  value,
  onChange,
  hint,
  lookingLabel,
  failedLabel,
}: {
  value: ChosenPlace | null;
  onChange: (place: ChosenPlace) => void;
  hint: string;
  lookingLabel: string;
  failedLabel: string;
}) {
  const { t } = useLocale();
  const searchId = useId();
  const [MapCanvas, setMapCanvas] = useState<ComponentType<MapProps> | null>(null);
  const [status, setStatus] = useState<'idle' | 'looking' | 'error'>('idle');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRoot = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<MapView | null>(null);
  const [frame, setFrame] = useState<MapFrame | null>(null);
  const [nearby, setNearby] = useState<NearbyAgency[]>([]);
  const [nearbyStatus, setNearbyStatus] = useState<'zoom' | 'loading' | 'ready' | 'empty' | 'error'>('zoom');
  const lookup = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void import('@/components/maps/LocationMap').then((module) => {
      if (!cancelled) setMapCanvas(() => module.LocationMap);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void fetch(`/api/places/search?q=${encodeURIComponent(term)}`)
        .then((response) => response.json())
        .then((body: unknown) => setResults(readSearch(body)))
        .catch(() => setResults([]));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    setActiveIndex(results.length > 0 ? 0 : -1);
  }, [results]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!searchRoot.current?.contains(event.target as Node)) setResults([]);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  useEffect(() => {
    if (view === null || view.zoom < NEARBY_MIN_ZOOM) {
      setNearby([]);
      setNearbyStatus('zoom');
      return;
    }
    const handle = window.setTimeout(() => {
      setNearbyStatus('loading');
      const params = new URLSearchParams({
        south: String(view.south),
        west: String(view.west),
        north: String(view.north),
        east: String(view.east),
      });
      void fetch(`/api/places/nearby?${params.toString()}`)
        .then(async (response) => {
          const body: unknown = await response.json().catch(() => null);
          if (!response.ok) {
            setNearbyStatus('error');
            return;
          }
          const agencies = readNearby(body);
          setNearby(agencies);
          setNearbyStatus(agencies.length === 0 ? 'empty' : 'ready');
        })
        .catch(() => setNearbyStatus('error'));
    }, 450);
    return () => window.clearTimeout(handle);
  }, [view]);

  async function choose(latitude: number, longitude: number, name?: string) {
    const request = lookup.current + 1;
    lookup.current = request;
    setStatus('looking');
    setResults([]);
    try {
      const response = await fetch(`/api/places/reverse?lat=${latitude}&lng=${longitude}`);
      const body: unknown = await response.json().catch(() => null);
      if (request !== lookup.current) return;
      const place = readPlace(body);
      if (!response.ok || place === null) {
        setStatus('error');
        return;
      }
      onChange({ ...place, latitude, longitude, ...(name ? { name } : {}) });
      setStatus('idle');
    } catch {
      if (request === lookup.current) setStatus('error');
    }
  }

  function chooseSearch(hit: SearchHit) {
    setQuery('');
    setResults([]);
    setFrame({ latitude: hit.latitude, longitude: hit.longitude, zoom: hit.zoom });
    if (hit.selectable) {
      onChange({
        name: hit.label.split(',')[0]?.trim(),
        city: hit.city,
        address: hit.address,
        country: hit.country,
        latitude: hit.latitude,
        longitude: hit.longitude,
      });
    }
  }

  function rememberView(next: MapView) {
    setView((current) => (current !== null && viewKey(current) === viewKey(next) ? current : next));
  }

  function onSearchKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setResults([]);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const hit = results[activeIndex];
      if (hit) chooseSearch(hit);
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? results.length - 1 : index - 1));
    }
  }

  const searchLabel = t('landing.contact.place.search');

  return (
    <div className="@container">
      <div className="mb-2 grid gap-2 @min-[40rem]:grid-cols-2 @min-[40rem]:items-start">
      <div ref={searchRoot} className="relative z-30">
        <Search size={16} aria-hidden className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input
          id={searchId}
          type="search"
          role="combobox"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onSearchKey}
          placeholder={searchLabel}
          aria-label={searchLabel}
          aria-expanded={results.length > 0}
          aria-controls={`${searchId}-results`}
          aria-activedescendant={activeIndex >= 0 ? `${searchId}-hit-${activeIndex}` : undefined}
          className="w-full rounded-xl border border-stone-200 bg-white py-2 ps-9 pe-3 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus-visible:border-[#FF8A00] focus-visible:ring-2 focus-visible:ring-[#FF8A00]/20"
          autoComplete="off"
        />
        {results.length > 0 ? (
          <ul id={`${searchId}-results`} role="listbox" className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-stone-200 bg-white py-1 shadow-lg">
            {results.map((hit, index) => {
              const parts = searchParts(hit.label);
              const active = index === activeIndex;
              return (
                <li key={`${hit.latitude},${hit.longitude},${hit.label}`}>
                  <button
                    id={`${searchId}-hit-${index}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => chooseSearch(hit)}
                    className={`block w-full px-3 py-2 text-left ${active ? 'bg-stone-100' : 'hover:bg-stone-50'}`}
                  >
                    <span className="block text-sm font-medium text-stone-900">{parts.title}</span>
                    {parts.detail !== '' ? <span className="block text-xs text-stone-500">{parts.detail}</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <AgencySelect
        status={nearbyStatus}
        agencies={nearby}
        lookingLabel={lookingLabel}
        labels={{
          nearby: t('landing.contact.place.nearby'),
          zoom: t('landing.contact.place.zoom'),
          empty: t('landing.contact.place.empty'),
          failed: failedLabel,
        }}
        selected={value}
        onChoose={(agency) => void choose(agency.latitude, agency.longitude, agency.name)}
      />
      </div>
      <div className="relative z-0 isolate overflow-hidden rounded-2xl border border-stone-200">
        {MapCanvas ? (
          <MapCanvas
            latitude={value?.latitude ?? null}
            longitude={value?.longitude ?? null}
            onPick={(latitude, longitude) => void choose(latitude, longitude)}
            onView={rememberView}
            frame={frame}
            nearby={nearby}
            chosenName={value?.name ?? ''}
            onChooseNearby={(pin) => void choose(pin.latitude, pin.longitude, pin.name)}
            className="h-72 w-full"
          />
        ) : (
          <div className="h-72 bg-stone-100" />
        )}
      </div>
      <p className="mt-2 text-xs text-stone-500" aria-live="polite">{status === 'looking' ? lookingLabel : hint}</p>
      {status === 'error' ? <p className="mt-1 text-xs font-medium text-red-700">{failedLabel}</p> : null}
      {value ? <ChosenPlaceNote place={value} /> : null}
    </div>
  );
}

function ChosenPlaceNote({ place }: { place: ChosenPlace }) {
  const detail = place.name
    ? [place.address, place.city].filter((part) => part !== '').join(' · ')
    : place.address;
  return (
    <p className="place-selected mt-2 rounded-xl border border-[#fdba74] px-3 py-2">
      <span className="block text-sm font-semibold">{place.name || place.city}</span>
      {detail !== '' ? <span className="place-selected__meta mt-0.5 block text-xs">{detail}</span> : null}
    </p>
  );
}

function agencyPrompt(
  status: 'zoom' | 'loading' | 'ready' | 'empty' | 'error',
  labels: { nearby: string; zoom: string; empty: string; failed: string },
  lookingLabel: string,
): string {
  if (status === 'zoom') return labels.zoom;
  if (status === 'loading') return lookingLabel;
  if (status === 'empty') return labels.empty;
  if (status === 'error') return labels.failed;
  return labels.nearby;
}

function AgencySelect({
  status,
  agencies,
  lookingLabel,
  labels,
  selected,
  onChoose,
}: {
  status: 'zoom' | 'loading' | 'ready' | 'empty' | 'error';
  agencies: NearbyAgency[];
  lookingLabel: string;
  labels: { nearby: string; zoom: string; empty: string; failed: string };
  selected: ChosenPlace | null;
  onChoose: (agency: NearbyAgency) => void;
}) {
  const selectId = useId();
  const prompt = agencyPrompt(status, labels, lookingLabel);
  const selectedId = agencies.find((agency) => (
    selected !== null && sameMapPoint(selected.latitude, selected.longitude, agency.latitude, agency.longitude)
  ))?.id ?? '';
  const canChoose = status === 'ready' && agencies.length > 0;

  return (
    <div className="relative z-20">
      <label htmlFor={selectId} className="sr-only">{labels.nearby}</label>
      <select
        id={selectId}
        value={selectedId}
        disabled={!canChoose}
        aria-label={labels.nearby}
        onChange={(event) => {
          const agency = agencies.find((item) => item.id === event.target.value);
          if (agency) onChoose(agency);
        }}
        className="w-full rounded-xl border border-stone-200 bg-white py-2 ps-3 pe-8 text-sm text-stone-900 outline-none focus-visible:border-[#FF8A00] focus-visible:ring-2 focus-visible:ring-[#FF8A00]/20 disabled:text-stone-500"
      >
        <option value="">{prompt}</option>
        {agencies.map((agency) => (
          <option key={agency.id} value={agency.id}>
            {agency.city !== '' ? `${agency.name} — ${agency.city}` : agency.name}
          </option>
        ))}
      </select>
    </div>
  );
}
